import React, { useEffect, useState } from 'react';
import { getDictionary, getDictionaryStats, DictionaryItem, DictionaryStats } from '../services/api';
import { speak } from '../services/audio';
import './Dictionary.css';

type ViewMode = 'alphabetical' | 'lesson';
type LanguageMode = 'en-pl' | 'pl-en';
type TypeFilter = 'all' | 'word' | 'sentence';

const Dictionary: React.FC = () => {
  const [items, setItems] = useState<DictionaryItem[]>([]);
  const [stats, setStats] = useState<DictionaryStats | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('alphabetical');
  const [languageMode, setLanguageMode] = useState<LanguageMode>('en-pl');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadData();
  }, [viewMode, languageMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const sortLang = languageMode === 'pl-en' ? 'polish' : 'english';
      const [itemsData, statsData] = await Promise.all([
        getDictionary(viewMode, sortLang),
        getDictionaryStats()
      ]);
      setItems(itemsData);
      setStats(statsData);
    } catch (err) {
      console.error('Failed to load dictionary:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = items.filter(item => {
    // Type filter
    if (typeFilter !== 'all' && item.type !== typeFilter) return false;
    // Search filter
    const query = searchQuery.toLowerCase();
    return item.english.toLowerCase().includes(query) ||
           item.polish.toLowerCase().includes(query);
  });

  const getSuccessRate = (item: DictionaryItem): number => {
    if (item.times_studied === 0) return 0;
    return Math.round((item.times_correct / item.times_studied) * 100);
  };

  const getMasteryColor = (mastery: number): string => {
    if (mastery >= 0.8) return '#4CAF50';
    if (mastery >= 0.5) return '#FF9800';
    return '#F44336';
  };

  const groupedByLesson = (): Map<string, DictionaryItem[]> => {
    const groups = new Map<string, DictionaryItem[]>();
    filteredItems.forEach(item => {
      const key = item.lesson_name || 'Inne';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(item);
    });
    return groups;
  };

  const groupedByLetter = (): Map<string, DictionaryItem[]> => {
    const groups = new Map<string, DictionaryItem[]>();
    filteredItems.forEach(item => {
      const word = languageMode === 'pl-en' ? item.polish : item.english;
      const letter = word.charAt(0).toUpperCase();
      if (!groups.has(letter)) groups.set(letter, []);
      groups.get(letter)!.push(item);
    });
    return groups;
  };

  if (loading) {
    return <div className="loading">Ładowanie słownika...</div>;
  }

  return (
    <div className="dictionary-page fade-in">
      <h1>📖 Słownik</h1>
      <p className="subtitle">Twoje nauczone słówka i zdania</p>

      {stats && (
        <div className="dictionary-stats">
          <div className="stat-item">
            <span className="stat-value">{stats.words_count || 0}</span>
            <span className="stat-label">słówek</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.sentences_count || 0}</span>
            <span className="stat-label">zdań</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{stats.avg_mastery || 0}%</span>
            <span className="stat-label">opanowanie</span>
          </div>
        </div>
      )}

      <div className="dictionary-controls">
        <input
          type="text"
          className="search-input"
          placeholder="🔍 Szukaj..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="control-buttons">
          <div className="toggle-group">
            <button
              className={`toggle-btn ${viewMode === 'alphabetical' ? 'active' : ''}`}
              onClick={() => setViewMode('alphabetical')}
            >
              🔤 Alfabetycznie
            </button>
            <button
              className={`toggle-btn ${viewMode === 'lesson' ? 'active' : ''}`}
              onClick={() => setViewMode('lesson')}
            >
              📚 Wg lekcji
            </button>
          </div>
          <div className="toggle-group">
            <button
              className={`toggle-btn ${languageMode === 'en-pl' ? 'active' : ''}`}
              onClick={() => setLanguageMode('en-pl')}
            >
              🇬🇧→🇵🇱
            </button>
            <button
              className={`toggle-btn ${languageMode === 'pl-en' ? 'active' : ''}`}
              onClick={() => setLanguageMode('pl-en')}
            >
              🇵🇱→🇬🇧
            </button>
          </div>
          <div className="toggle-group">
            <button
              className={`toggle-btn ${typeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTypeFilter('all')}
            >
              📝 Wszystko
            </button>
            <button
              className={`toggle-btn ${typeFilter === 'word' ? 'active' : ''}`}
              onClick={() => setTypeFilter('word')}
            >
              🔤 Słowa
            </button>
            <button
              className={`toggle-btn ${typeFilter === 'sentence' ? 'active' : ''}`}
              onClick={() => setTypeFilter('sentence')}
            >
              💬 Zdania
            </button>
          </div>
        </div>
      </div>

      {filteredItems.length === 0 ? (
        <div className="no-items">
          <div className="no-items-icon">📭</div>
          <h2>Brak słówek w słowniku</h2>
          <p>Zagraj w gry, aby dodać słówka do słownika!</p>
        </div>
      ) : viewMode === 'alphabetical' ? (
        <div className="dictionary-grouped">
          {Array.from(groupedByLetter().entries()).map(([letter, letterItems]) => (
            <div key={letter} className="letter-group">
              <div className="letter-separator">{letter}</div>
              <div className="dictionary-list">
                {letterItems.map(item => (
                  <DictionaryEntry key={item.id} item={item} languageMode={languageMode}
                    getSuccessRate={getSuccessRate} getMasteryColor={getMasteryColor} />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="dictionary-grouped">
          {Array.from(groupedByLesson().entries()).map(([lessonName, lessonItems]) => (
            <div key={lessonName} className="lesson-group">
              <h3 className="lesson-group-title">{lessonName}</h3>
              <div className="dictionary-list">
                {lessonItems.map(item => (
                  <DictionaryEntry key={item.id} item={item} languageMode={languageMode}
                    getSuccessRate={getSuccessRate} getMasteryColor={getMasteryColor} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Dictionary entry component
interface DictionaryEntryProps {
  item: DictionaryItem;
  languageMode: LanguageMode;
  getSuccessRate: (item: DictionaryItem) => number;
  getMasteryColor: (mastery: number) => string;
}

const DictionaryEntry: React.FC<DictionaryEntryProps> = ({
  item, languageMode, getSuccessRate, getMasteryColor
}) => {
  const primary = languageMode === 'en-pl' ? item.english : item.polish;
  const secondary = languageMode === 'en-pl' ? item.polish : item.english;
  const successRate = getSuccessRate(item);
  const failures = item.times_studied - item.times_correct;

  return (
    <div className={`dictionary-entry ${item.type}`}>
      <div className="entry-main">
        <div className="entry-words">
          <span className="entry-primary">{primary}</span>
          <span className="entry-separator">—</span>
          <span className="entry-secondary">{secondary}</span>
        </div>
        <button className="speak-btn" onClick={() => speak(item.english)} title="Posłuchaj">
          🔊
        </button>
      </div>
      <div className="entry-stats">
        <span
          className="stat-badge"
          style={{ backgroundColor: getMasteryColor(item.mastery_level) }}
          title={`Poziom opanowania: ${Math.round(item.mastery_level * 100)}%\n\nPokazuje jak dobrze znasz to słówko.\nRośnie gdy odpowiadasz poprawnie,\nmaleje przy błędach.`}
        >
          {Math.round(item.mastery_level * 100)}%
        </span>
        <span
          className="stat-item"
          title={`Poprawne odpowiedzi: ${item.times_correct}\n\nLiczba razy, gdy poprawnie\nodpowiedziałeś na to słówko w grach.`}
        >
          ✅ {item.times_correct}
        </span>
        <span
          className="stat-item"
          title={`Błędne odpowiedzi: ${failures}\n\nLiczba razy, gdy pomyliłeś\nsię przy tym słówku w grach.`}
        >
          ❌ {failures}
        </span>
        <span
          className="stat-item"
          title={`Skuteczność: ${successRate}%\n\nProcent poprawnych odpowiedzi.\nObliczane jako: poprawne / wszystkie × 100%`}
        >
          📊 {successRate}%
        </span>
        {item.type === 'sentence' && <span className="type-badge">zdanie</span>}
      </div>
    </div>
  );
};

export default Dictionary;

